import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ConfirmModal from '../components/ConfirmModal';
import { remindersApi, twoOneOneApi, confirmApi } from '../services/api';

// Shared with PrepHistory so reopening a session lands the EP page on it.
export const STORAGE_KEY = '211_session_id';

const STEP_INFO = [
  { label: '1st Dose', pills: 2, sublabel: '2 pills now' },
  { label: '+24 hours', pills: 1, sublabel: '1 pill tomorrow' },
  { label: '+48 hours', pills: 1, sublabel: '1 pill day after' },
];

const getStepInfo = (i) => STEP_INFO[i] ?? { label: `+${i * 24} hours`, pills: 1, sublabel: '1 pill' };

const isToday = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr).toDateString() === new Date().toDateString();
};

const PillIcon = ({ size = 'md' }) => {
  const w = size === 'sm' ? 16 : 20;
  const h = size === 'sm' ? 8 : 10;
  return (
    <svg width={w} height={h} viewBox="0 0 22 11" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="0.5" width="21" height="10" rx="5" fill="#3B82F6" stroke="#1D4ED8" />
      <line x1="11" y1="0.5" x2="11" y2="10.5" stroke="#1D4ED8" strokeWidth="1" />
    </svg>
  );
};

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const NoteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const STATUS_CONFIG = {
  due:       { badge: 'bg-amber-50 text-amber-700 border-amber-200',  step: 'bg-gray-100 text-gray-500 border-gray-200' },
  sent:      { badge: 'bg-blue-50 text-blue-700 border-blue-200',     step: 'bg-blue-500 text-white border-blue-600'  },
  confirmed: { badge: 'bg-green-50 text-green-700 border-green-200',  step: 'bg-green-500 text-white border-green-600' },
};

const STATUS_LABELS = { due: 'Due', sent: 'Sent', confirmed: 'Done' };

const ReminderStep = ({ row, stepInfo, stepNum, isLast, onConfirm, confirming, onManualConfirm, manualConfirming }) => {
  const canConfirm = row.status === 'sent' && row.token;
  const canManualConfirm = row.status === 'due' && isToday(row.next_remind_at);
  const cfg = STATUS_CONFIG[row.status];
  const isConfirmed = row.status === 'confirmed';

  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center flex-shrink-0">
        <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center text-sm font-bold transition-all ${cfg.step}`}>
          {isConfirmed ? <CheckIcon /> : stepNum}
        </div>
        {!isLast && (
          <div className={`w-0.5 flex-1 my-1.5 min-h-8 rounded-full transition-colors ${
            isConfirmed ? 'bg-green-300' : 'bg-gray-200'
          }`} />
        )}
      </div>

      <div className={`flex-1 mb-4 rounded-xl border px-5 py-4 transition-all ${
        isConfirmed ? 'bg-green-50 border-green-200' : 'bg-white border-gray-100 shadow-sm'
      }`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className={`font-semibold ${isConfirmed ? 'text-green-900' : 'text-gray-900'}`}>
                {stepInfo.label}
              </span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.badge}`}>
                {STATUS_LABELS[row.status]}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-2.5">
              {Array.from({ length: stepInfo.pills }).map((_, i) => (
                <PillIcon key={i} size="sm" />
              ))}
              <span className="text-xs text-gray-400 ml-0.5">{stepInfo.sublabel}</span>
            </div>
            {row.next_remind_at && row.status === 'due' && (
              <p className="text-xs text-gray-400 mt-1.5">
                Reminder at {new Date(row.next_remind_at).toLocaleString()}
              </p>
            )}
            {row.confirmed_at && (
              <p className="text-xs text-green-600 mt-1.5">
                Confirmed {new Date(row.confirmed_at).toLocaleString()}
              </p>
            )}
          </div>
          {canConfirm && (
            <button
              onClick={() => onConfirm(row.token, row.id)}
              disabled={confirming === row.id}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition font-medium flex-shrink-0 shadow-sm"
            >
              {confirming === row.id ? '…' : 'Confirm'}
            </button>
          )}
          {canManualConfirm && (
            <button
              onClick={() => onManualConfirm(row.id)}
              disabled={manualConfirming === row.id}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition font-medium flex-shrink-0 shadow-sm"
            >
              {manualConfirming === row.id ? '…' : 'Confirm'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CommentModal = ({ initialValue, onSave, onClose }) => {
  const [draft, setDraft] = useState(initialValue);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(draft);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 max-w-sm w-full">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Session note</h3>
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Add a note for this session…"
          rows={4}
          autoFocus
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <div className="flex gap-3 mt-4">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TwoOneOne = () => {
  const [sessionId, setSessionId] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? parseInt(stored) : null;
    } catch {
      return null;
    }
  });

  const [rows, setRows] = useState([]);
  const [sessionComment, setSessionComment] = useState('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [confirmError, setConfirmError] = useState('');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closing, setClosing] = useState(false);

  const fetchStatus = useCallback(async (id) => {
    try {
      const res = await twoOneOneApi.sessionStatus(id);
      setRows(res.data.reminders);
      setSessionComment(res.data.comment ?? '');
    } catch (err) {
      const status = err.response?.status;
      if (status === 404 || status === 410) {
        // Session deleted or closed — clear stale local state and show start screen
        localStorage.removeItem(STORAGE_KEY);
        setSessionId(null);
        setRows([]);
        setSessionComment('');
      }
      // silently ignore other transient poll errors
    }
  }, []);

  // On mount, if no local session ID, check the server for the most recent one
  useEffect(() => {
    if (sessionId !== null) return;
    twoOneOneApi.currentSession().then(res => {
      if (res.data?.id) {
        const id = res.data.id;
        localStorage.setItem(STORAGE_KEY, String(id));
        setSessionId(id);
        setSessionComment(res.data.comment ?? '');
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sessionId) return;
    fetchStatus(sessionId);
    const interval = setInterval(() => fetchStatus(sessionId), 15000);
    return () => clearInterval(interval);
  }, [sessionId, fetchStatus]);

  const handleStart = async () => {
    setStarting(true);
    setStartError('');
    try {
      const sessionRes = await twoOneOneApi.createSession();
      const newSessionId = sessionRes.data.id;

      const now = new Date();
      const in10s = new Date(now.getTime() + 10 * 1000).toISOString();

      const tomorrow9am = new Date(now);
      tomorrow9am.setDate(tomorrow9am.getDate() + 1);
      tomorrow9am.setHours(9, 0, 0, 0);

      const dayAfter9am = new Date(now);
      dayAfter9am.setDate(dayAfter9am.getDate() + 2);
      dayAfter9am.setHours(9, 0, 0, 0);

      const base = {
        frequency: 'once',
        notify_email: false,
        notify_telegram: true,
        include_confirm_link: true,
        confirm_path: 'confirm',
        prep_session_id: newSessionId,
      };

      await Promise.all([
        remindersApi.create({ ...base, name: '2 Prep', remind_at: in10s }),
        remindersApi.create({ ...base, name: '1 Prep', remind_at: tomorrow9am.toISOString() }),
        remindersApi.create({ ...base, name: '1 Prep', remind_at: dayAfter9am.toISOString() }),
      ]);

      localStorage.setItem(STORAGE_KEY, String(newSessionId));
      setSessionId(newSessionId);
      setSessionComment('');
      await fetchStatus(newSessionId);
    } catch (err) {
      const details = err.response?.data?.details;
      setStartError(details ? details.join(', ') : 'Failed to create reminders.');
    } finally {
      setStarting(false);
    }
  };

  const handleConfirm = async (token, id) => {
    setConfirming(id);
    setConfirmError('');
    try {
      await confirmApi.confirm(token);
      await fetchStatus(sessionId);
      console.log(`[Confirm] Dose confirmed via token, reminder_id=${id}`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Confirmation failed. Please try again.';
      console.error(`[Confirm] Failed to confirm reminder_id=${id}:`, msg);
      setConfirmError(msg);
    } finally {
      setConfirming(null);
    }
  };

  const handleClose = async () => {
    setClosing(true);
    setShowCloseConfirm(false);
    try {
      await twoOneOneApi.closeSession(sessionId);
    } catch {
      // ignore — reminders may already be inactive
    } finally {
      localStorage.removeItem(STORAGE_KEY);
      setSessionId(null);
      setRows([]);
      setSessionComment('');
      setClosing(false);
    }
  };

  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    setShowConfirm(false);
    try {
      await twoOneOneApi.deleteSession(sessionId);
    } catch {
      // ignore — session may already be gone on the server
    } finally {
      localStorage.removeItem(STORAGE_KEY);
      setSessionId(null);
      setRows([]);
      setSessionComment('');
      setDeleting(false);
    }
  };

  const [manualConfirming, setManualConfirming] = useState(null);

  const handleManualConfirm = async (reminderId) => {
    setManualConfirming(reminderId);
    setConfirmError('');
    try {
      await twoOneOneApi.manualConfirm(sessionId, reminderId);
      await fetchStatus(sessionId);
      console.log(`[Confirm] Manual confirm success, reminder_id=${reminderId}`);
    } catch (err) {
      const msg = err.response?.data?.error || 'Confirmation failed. Please try again.';
      console.error(`[Confirm] Manual confirm failed, reminder_id=${reminderId}:`, msg);
      setConfirmError(msg);
    } finally {
      setManualConfirming(null);
    }
  };

  const [extending, setExtending] = useState(false);

  const handleExtend = async () => {
    setExtending(true);
    try {
      await twoOneOneApi.extendSession(sessionId);
      await fetchStatus(sessionId);
    } catch {
      // ignore
    } finally {
      setExtending(false);
    }
  };

  const handleSaveComment = async (text) => {
    try {
      await twoOneOneApi.updateComment(sessionId, text);
      setSessionComment(text);
    } catch {
      // ignore
    } finally {
      setShowCommentModal(false);
    }
  };

  const allDone = rows.length > 0 && rows.every(r => r.status === 'confirmed');

  return (
    <div className="min-h-screen bg-gray-50">
      {showCloseConfirm && (
        <ConfirmModal
          message="Close this session? Pending reminders will be cancelled."
          confirmLabel="Close session"
          variant="neutral"
          onConfirm={handleClose}
          onCancel={() => setShowCloseConfirm(false)}
        />
      )}
      {showConfirm && (
        <ConfirmModal
          message="Delete this session and all its reminders?"
          onConfirm={handleDelete}
          onCancel={() => setShowConfirm(false)}
        />
      )}
      {showCommentModal && (
        <CommentModal
          initialValue={sessionComment}
          onSave={handleSaveComment}
          onClose={() => setShowCommentModal(false)}
        />
      )}
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">EP</h1>
          </div>
          <Link to="/ep/history" className="text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800 transition bg-white shadow-sm">
            History
          </Link>
        </div>

        {!sessionId ? (
          /* ── Start screen ── */
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="flex flex-col items-center gap-2">
                <div className="flex gap-1.5"><PillIcon /><PillIcon /></div>
                <span className="text-xs text-gray-400 font-medium">1st Dose</span>
              </div>
              <div className="text-gray-200 text-xl font-light mx-1">→</div>
              <div className="flex flex-col items-center gap-2">
                <PillIcon />
                <span className="text-xs text-gray-400 font-medium">+24h</span>
              </div>
              <div className="text-gray-200 text-xl font-light mx-1">→</div>
              <div className="flex flex-col items-center gap-2">
                <PillIcon />
                <span className="text-xs text-gray-400 font-medium">+48h</span>
              </div>
            </div>
            {startError && (
              <p className="text-red-600 text-sm mb-4 text-center">{startError}</p>
            )}
            <button
              onClick={handleStart}
              disabled={starting}
              className="w-full py-3.5 bg-primary-600 text-white text-base font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-60 transition shadow-sm"
            >
              {starting ? 'Starting…' : 'Start EP'}
            </button>
          </div>

        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">Loading…</div>

        ) : (
          <div>
            {rows.map((row, i) => (
              <ReminderStep
                key={row.id}
                row={row}
                stepInfo={getStepInfo(i)}
                stepNum={i + 1}
                isLast={i === rows.length - 1}
                onConfirm={handleConfirm}
                confirming={confirming}
                onManualConfirm={handleManualConfirm}
                manualConfirming={manualConfirming}
              />
            ))}

            {confirmError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 text-center">
                {confirmError}
              </div>
            )}

            {allDone && (
              <div className="mt-1 bg-green-50 border border-green-200 rounded-xl px-5 py-4 text-center">
                <p className="text-green-800 font-semibold">Protocol complete</p>
                <p className="text-green-600 text-sm mt-0.5">All doses confirmed.</p>
              </div>
            )}

            {/* Session note */}
            {sessionComment && (
              <div className="mt-4 bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm">
                <p className="text-xs text-gray-400 mb-1">Note</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{sessionComment}</p>
              </div>
            )}

            <div className="pt-6 flex justify-center gap-3 flex-wrap">
              <button
                onClick={() => setShowCloseConfirm(true)}
                disabled={closing}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800 transition bg-white shadow-sm disabled:opacity-50"
              >
                {closing ? 'Closing…' : 'Close'}
              </button>
              <button
                onClick={() => setShowCommentModal(true)}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800 transition bg-white shadow-sm flex items-center gap-1.5"
              >
                <NoteIcon />
                {sessionComment ? 'Edit note' : 'Add note'}
              </button>
              <button
                onClick={handleExtend}
                disabled={extending}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800 transition bg-white shadow-sm disabled:opacity-50"
              >
                {extending ? 'Adding…' : 'Extend'}
              </button>
              <button
                onClick={() => setShowConfirm(true)}
                disabled={deleting}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transition bg-white shadow-sm disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoOneOne;
